import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, Form, Button, ButtonGroup } from 'react-bootstrap';
import { Calendar } from 'lucide-react';
import moment from 'moment-timezone';
import './DateRangePicker.css';

const TIMEZONE = 'Asia/Ho_Chi_Minh';

export const PRESETS = [
  { value: 'today', label: 'Hôm nay' },
  { value: 'yesterday', label: 'Hôm qua' },
  { value: 'last7days', label: '7 ngày trước' },
  { value: 'thisWeek', label: 'Tuần này' },
  { value: 'lastWeek', label: 'Tuần trước' },
  { value: 'last30days', label: '30 ngày trước' },
  { value: 'thisMonth', label: 'Tháng này' },
  { value: 'lastMonth', label: 'Tháng trước' },
  { value: 'last3months', label: '3 tháng' },
  { value: 'last6months', label: '6 tháng' },
  { value: 'last12months', label: '12 tháng' },
];

export const calcDatePreset = (preset) => {
  const today = moment.tz(TIMEZONE);
  switch (preset) {
    case 'today':
      return { from: today.format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
    case 'yesterday': {
      const d = today.clone().subtract(1, 'days').format('YYYY-MM-DD');
      return { from: d, to: d };
    }
    case 'last7days':
      return {
        from: today.clone().subtract(6, 'days').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      };
    case 'thisWeek':
      return {
        from: today.clone().startOf('isoWeek').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      };
    case 'lastWeek':
      return {
        from: today.clone().subtract(1, 'week').startOf('isoWeek').format('YYYY-MM-DD'),
        to: today.clone().subtract(1, 'week').endOf('isoWeek').format('YYYY-MM-DD'),
      };
    case 'thisMonth':
      return {
        from: today.clone().startOf('month').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      };
    case 'lastMonth':
      return {
        from: today.clone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        to: today.clone().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      };
    case 'last30days':
      return {
        from: today.clone().subtract(29, 'days').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      };
    case 'last3months':
      return {
        from: today.clone().subtract(3, 'months').add(1, 'days').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      };
    case 'last6months':
      return {
        from: today.clone().subtract(6, 'months').add(1, 'days').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      };
    case 'last12months':
      return {
        from: today.clone().subtract(12, 'months').add(1, 'days').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      };
    default:
      return { from: today.format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
  }
};

/**
 * DateRangePicker - Component chọn khoảng thời gian tái sử dụng
 * @param {string} fromDate - Ngày bắt đầu YYYY-MM-DD
 * @param {string} toDate - Ngày kết thúc YYYY-MM-DD
 * @param {function} onChange - Callback(from: string, to: string) khi thay đổi
 * @param {string} defaultPreset - Preset mặc định khi khởi tạo ('today', 'last7days', ...)
 * @param {boolean} compact - #150507 (góp ý Spot Fix Trang chủ): bản GỌN cho điện thoại — preset
 *   thành 1 hàng chip vuốt ngang (máy tính thì tự xuống dòng), 2 ô ngày nằm chung 1 hàng, dòng tiêu
 *   đề kèm khoảng ngày đang xem. Mặc định false = giữ nguyên giao diện cũ cho các màn khác.
 */
const DateRangePicker = ({ fromDate, toDate, onChange, defaultPreset = null, compact = false }) => {
  const [activePreset, setActivePreset] = useState(defaultPreset);
  const chipsRef = useRef(null);

  // Hàng chip vuốt ngang: giữ chip đang chọn luôn nằm trong tầm nhìn (vd chọn "12 tháng" ở cuối).
  useEffect(() => {
    if (!compact || !chipsRef.current) return;
    const active = chipsRef.current.querySelector('.drp-chip.is-active');
    if (!active) return;
    const box = chipsRef.current;
    const a = active.getBoundingClientRect();
    const b = box.getBoundingClientRect();
    if (a.left < b.left || a.right > b.right) {
      box.scrollLeft = Math.max(0, box.scrollLeft + a.left - b.left - 16);
    }
  }, [compact, activePreset]);

  const handlePreset = (preset) => {
    setActivePreset(preset);
    const { from, to } = calcDatePreset(preset);
    onChange(from, to);
  };

  const handleFromChange = (e) => {
    setActivePreset(null);
    onChange(e.target.value, toDate);
  };

  const handleToChange = (e) => {
    setActivePreset(null);
    onChange(fromDate, e.target.value);
  };

  if (compact) {
    // #210811 (góp ý Spot Fix Trang chủ): hiển thị ngày dạng ngắn dd/mm/yy thay cho kiểu ISO
    // (2026-09-23) của ô ngày gốc trình duyệt — ô ngày gốc vẫn nằm dưới (trong suốt) để mở lịch.
    const fmt = (d) => (d ? moment(d, 'YYYY-MM-DD').format('DD/MM/YY') : '—');
    const rangeText = fromDate && fromDate === toDate ? fmt(fromDate) : `${fmt(fromDate)} – ${fmt(toDate)}`;

    // Ô ngày gốc bị làm trong suốt nên tự mở lịch khi bấm (điện thoại: bánh xe ngày của hệ điều hành;
    // máy tính: lịch của trình duyệt). Trình duyệt cũ không có showPicker thì vẫn focus được như cũ.
    const openPicker = (e) => {
      const el = e.currentTarget;
      if (typeof el.showPicker === 'function') {
        try {
          el.showPicker();
        } catch (err) {
          /* Trình duyệt từ chối mở — người dùng bấm lại / dùng bàn phím. */
        }
      }
    };

    return (
      <div className="drp-compact">
        <div className="drp-compact__head">
          <span className="drp-compact__title">
            <Calendar size={15} />
            Khoảng thời gian
          </span>
          <span className="drp-compact__range">{rangeText}</span>
        </div>

        <div
          className="drp-compact__chips"
          role="group"
          aria-label="Chọn nhanh khoảng thời gian"
          ref={chipsRef}
        >
          {PRESETS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              className={`drp-chip${activePreset === preset.value ? ' is-active' : ''}`}
              aria-pressed={activePreset === preset.value}
              onClick={() => handlePreset(preset.value)}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <div className="drp-compact__dates">
          <label className="drp-date">
            <span className="drp-date__label">Từ ngày</span>
            <span className="drp-date__field">
              <span className="drp-date__value">{fmt(fromDate)}</span>
              <input
                type="date"
                className="drp-date__input"
                value={fromDate || ''}
                max={toDate || undefined}
                onChange={handleFromChange}
                onClick={openPicker}
                aria-label="Từ ngày"
              />
            </span>
          </label>
          <label className="drp-date">
            <span className="drp-date__label">Đến ngày</span>
            <span className="drp-date__field">
              <span className="drp-date__value">{fmt(toDate)}</span>
              <input
                type="date"
                className="drp-date__input"
                value={toDate || ''}
                min={fromDate || undefined}
                onChange={handleToChange}
                onClick={openPicker}
                aria-label="Đến ngày"
              />
            </span>
          </label>
        </div>
      </div>
    );
  }

  return (
    <Row>
      <Col md={12} className="mb-3">
        <Form.Group>
          <Form.Label>
            <Calendar size={16} className="me-1" />
            Khoảng thời gian
          </Form.Label>
          <ButtonGroup size="sm" className="w-100 flex-wrap">
            {PRESETS.map((preset) => (
              <Button
                key={preset.value}
                variant={activePreset === preset.value ? 'primary' : 'outline-primary'}
                onClick={() => handlePreset(preset.value)}
                className="flex-fill"
              >
                {preset.label}
              </Button>
            ))}
          </ButtonGroup>
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group>
          <Form.Label>Từ ngày</Form.Label>
          <Form.Control
            type="date"
            size="sm"
            value={fromDate}
            onChange={handleFromChange}
          />
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group>
          <Form.Label>Đến ngày</Form.Label>
          <Form.Control
            type="date"
            size="sm"
            value={toDate}
            onChange={handleToChange}
          />
        </Form.Group>
      </Col>
    </Row>
  );
};

export default DateRangePicker;
